import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { authService } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [tokenValid, setTokenValid] = useState(null); // null = checking
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [countdown, setCountdown] = useState(3);

  // Verify token on mount
  const verifyToken = useCallback(async () => {
    if (!token) {
      setTokenValid(false);
      return;
    }
    try {
      await authService.verifyResetToken(token);
      setTokenValid(true);
    } catch {
      setTokenValid(false);
    }
  }, [token]);

  useEffect(() => {
    verifyToken();
  }, [verifyToken]);

  // Countdown redirect after success
  useEffect(() => {
    if (!success) return;
    if (countdown <= 0) {
      navigate('/login');
      return;
    }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [success, countdown, navigate]);

  const validatePassword = (pwd) => {
    if (pwd.length < 8) return 'Mínimo 8 caracteres.';
    if (!/[A-Z]/.test(pwd)) return 'Debe contener al menos una mayúscula.';
    if (!/[0-9]/.test(pwd)) return 'Debe contener al menos un número.';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const pwdError = validatePassword(password);
    if (pwdError) { setError(pwdError); return; }
    if (password !== confirm) { setError('Las contraseñas no coinciden.'); return; }

    setIsLoading(true);
    try {
      await authService.resetPasswordWithToken(token, password);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Token inválido o expirado. Solicita un nuevo enlace.');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Checking token ───────────────────────────────────────────────────────
  if (tokenValid === null) {
    return (
      <div className="min-h-screen bg-[#0a0e17] flex items-center justify-center">
        <p className="text-[#b0b0b0] animate-pulse">Verificando enlace…</p>
      </div>
    );
  }

  // ── Invalid / expired token ──────────────────────────────────────────────
  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-[#0a0e17] flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-[#1a2332] rounded-lg p-8 border border-[#2a3444] text-center">
          <h1 className="text-2xl font-bold text-red-400 mb-3">Enlace inválido</h1>
          <p className="text-[#b0b0b0] mb-6">
            Este enlace de restablecimiento no es válido o ya expiró. Los enlaces son válidos por 15 minutos.
          </p>
          <Link
            to="/forgot-password"
            className="inline-block bg-[#d4af37] text-[#0a0e17] px-6 py-2 font-semibold hover:bg-[#e5c158]"
          >
            Solicitar nuevo enlace
          </Link>
        </div>
      </div>
    );
  }

  // ── Success ──────────────────────────────────────────────────────────────
  if (success) {
    return (
      <div className="min-h-screen bg-[#0a0e17] flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-[#1a2332] rounded-lg p-8 border border-[#2a3444] text-center">
          <div className="w-16 h-16 bg-[#d4af37]/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-[#d4af37]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-[#f5f5f0] mb-3">¡Contraseña restablecida!</h1>
          <p className="text-[#b0b0b0] mb-2">
            Tu contraseña fue actualizada correctamente.
          </p>
          <p className="text-[#b0b0b0] mb-6">
            Redirigiendo al login en{' '}
            <span className="text-[#d4af37] font-bold">{countdown}</span> segundos…
          </p>
          <Link to="/login" className="text-[#d4af37] hover:underline text-sm">
            Ir al login ahora
          </Link>
        </div>
      </div>
    );
  }

  // ── Password form ────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#0a0e17] flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-[#1a2332] rounded-lg p-8 border border-[#2a3444]">
        <h1 className="text-3xl font-bold text-[#f5f5f0] mb-2">Nueva contraseña</h1>
        <p className="text-[#b0b0b0] mb-8">
          Elige una contraseña segura. Mínimo 8 caracteres, una mayúscula y un número.
        </p>

        {error && (
          <Alert className="mb-6 border-red-500 bg-red-500/10">
            <AlertDescription className="text-red-400">{error}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="password" className="text-[#f5f5f0]">
              Nueva contraseña
            </Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="bg-[#0a0e17] border-[#2a3444] text-[#f5f5f0]"
              autoFocus
            />
          </div>

          <div>
            <Label htmlFor="confirm" className="text-[#f5f5f0]">
              Confirmar contraseña
            </Label>
            <Input
              id="confirm"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="••••••••"
              className="bg-[#0a0e17] border-[#2a3444] text-[#f5f5f0]"
            />
          </div>

          <Button
            type="submit"
            disabled={isLoading}
            className="w-full bg-[#d4af37] text-[#0a0e17] hover:bg-[#e5c158] font-semibold"
          >
            {isLoading ? 'Guardando…' : 'Restablecer contraseña'}
          </Button>
        </form>
      </div>
    </div>
  );
}
